import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { providerApiClient } from './provider-client'
import type {
  ValidateCredentialsRequest,
  ProviderEventsRequest,
  CreateCredentialRequest,
  CreateIntegrationRequest,
  UpdateTierRequest,
  IntegrationsParams,
  TicketsParams,
} from './provider-types'

// Query Keys
export const providerQueryKeys = {
  credentials: (orgId?: number) => ['providerCredentials', orgId] as const,
  integrations: (eventId: number, params?: IntegrationsParams) => ['integrations', eventId, params] as const,
  integration: (id: number) => ['integration', id] as const,
  tiers: (integrationId: number) => ['integrationTiers', integrationId] as const,
  tickets: (integrationId: number, params?: TicketsParams) => ['integrationTickets', integrationId, params] as const,
  ticketStats: (integrationId: number) => ['integrationTicketStats', integrationId] as const,
  syncLogs: (integrationId: number) => ['integrationSyncLogs', integrationId] as const,
}

// --- Credential Queries ---

export function useProviderCredentials(orgId?: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: providerQueryKeys.credentials(orgId),
    queryFn: () => providerApiClient.listCredentials(token!, { organization: orgId }),
    enabled: !!token && !!orgId,
  })
}

export function useCreateCredential() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCredentialRequest) => providerApiClient.createCredential(token!, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.credentials(variables.organization) })
    },
  })
}

export function useDeleteCredential() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => providerApiClient.deleteCredential(token!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providerCredentials'] })
    },
  })
}

// --- Validation & Discovery Mutations ---

export function useValidateCredentials() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: (data: ValidateCredentialsRequest) => providerApiClient.validateCredentials(token!, data),
  })
}

export function useGetProviderEvents() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: (data: ProviderEventsRequest) => providerApiClient.getProviderEvents(token!, data),
  })
}

// --- Integration Queries ---

export function useIntegrations(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: providerQueryKeys.integrations(eventId),
    queryFn: () => providerApiClient.listIntegrations(token!, { event: eventId }),
    enabled: !!token && !!eventId,
  })
}

export function useIntegration(id: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: providerQueryKeys.integration(id),
    queryFn: () => providerApiClient.getIntegration(token!, id),
    enabled: !!token && !!id,
  })
}

export function useCreateIntegration() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateIntegrationRequest) => providerApiClient.createIntegration(token!, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.integrations(variables.event) })
    },
  })
}

export function useUpdateIntegration() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ is_enabled: boolean }> }) =>
      providerApiClient.updateIntegration(token!, id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.integration(result.id) })
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.integrations(result.event) })
    },
  })
}

export function useDeleteIntegration() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, eventId }: { id: number; eventId: number }) =>
      providerApiClient.deleteIntegration(token!, id).then(() => eventId),
    onSuccess: (eventId) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.integrations(eventId) })
    },
  })
}

// --- Tier Queries ---

export function useIntegrationTiers(integrationId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: providerQueryKeys.tiers(integrationId),
    queryFn: () => providerApiClient.getIntegrationTiers(token!, integrationId),
    enabled: !!token && !!integrationId,
  })
}

export function useSyncTiers() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (integrationId: number) => providerApiClient.syncTiers(token!, integrationId),
    onSuccess: (_data, integrationId) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.tiers(integrationId) })
    },
  })
}

export function useUpdateTier() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ integrationId, tierId, data }: { integrationId: number; tierId: number; data: UpdateTierRequest }) =>
      providerApiClient.updateTier(token!, integrationId, tierId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.tiers(variables.integrationId) })
    },
  })
}

// --- Ticket Queries ---

export function useIntegrationTickets(integrationId: number, params?: TicketsParams) {
  const { token } = useAuth()
  return useQuery({
    queryKey: providerQueryKeys.tickets(integrationId, params),
    queryFn: () => providerApiClient.getTickets(token!, integrationId, params),
    enabled: !!token && !!integrationId,
  })
}

export function useTicketStats(integrationId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: providerQueryKeys.ticketStats(integrationId),
    queryFn: () => providerApiClient.getTicketStats(token!, integrationId),
    enabled: !!token && !!integrationId,
  })
}

export function useMatchTicket() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ integrationId, ticketId }: { integrationId: number; ticketId: number }) =>
      providerApiClient.matchTicket(token!, integrationId, ticketId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.tickets(variables.integrationId) })
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.ticketStats(variables.integrationId) })
    },
  })
}

export function useCreditTickets() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ integrationId, ticketIds }: { integrationId: number; ticketIds: number[] }) =>
      providerApiClient.creditTickets(token!, integrationId, ticketIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.tickets(variables.integrationId) })
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.ticketStats(variables.integrationId) })
    },
  })
}

// --- Sync Queries ---

export function useSyncIntegration() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (integrationId: number) => providerApiClient.syncIntegration(token!, integrationId),
    onSuccess: (_data, integrationId) => {
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.syncLogs(integrationId) })
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.ticketStats(integrationId) })
      queryClient.invalidateQueries({ queryKey: providerQueryKeys.tickets(integrationId) })
    },
  })
}

export function useSyncLogs(integrationId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: providerQueryKeys.syncLogs(integrationId),
    queryFn: () => providerApiClient.getSyncLogs(token!, integrationId),
    enabled: !!token && !!integrationId,
  })
}
